// services/staff.service.ts

import {
  AsyncSelectFetchParams,
  AsyncSelectFetchResult,
} from "@/components/form/types";
import { axiosInstance } from "@/lib/axiosInstance";

export type StaffStatus = "active" | "inactive" | "on_leave";

export interface StaffType {
  id: string;
  recruiter_id: string;
  name: string;
  email: string;
  phone: string;
  designation: string;
  designation_id: string;
  designation_name: string;
  designation_rank: number;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  temp_password?: string;
  role_id?: string;
  role_name?: string;
}

export interface StaffCreateType {
  name: string;
  email?: string;
  phone: string;
  designation: string;
  designation_id: string;
  role_id?: string;
}

export interface StaffMetaType {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type StaffParamsType = {
  search?: string;
  q?: string;
  is_active?: boolean | string;
  status?: string;
  active_only?: boolean | string;
  designation_id?: string;
  department_id?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
};

export const fetchDesignationOptions = async ({
  search,
}: AsyncSelectFetchParams): Promise<AsyncSelectFetchResult> => {
  const res = await axiosInstance.get("/v1/designations", {
    params: { active_only: true, search },
  });

  let designations = res.data?.data || res.data || [];
  designations = designations.filter((d: any) => d.name !== "placement_officer");

  return {
    items: designations.map((d: any) => ({
      label: d.name,
      value: d.id,
    })),
    hasMore: false, // API currently returns a simple array
    total: designations.length,
  };
};

// ─── Service Functions ────────────────────────────────────────────────────────

export const fetchAllStaff = async (filters: StaffParamsType) => {
  const res = await axiosInstance.get("/v1/staff", { params: filters });
  const data = res.data;
  const staff = data.items || [];
  const total = data.total || 0;
  const page = data.page || 1;
  const limit = data.limit || 10;

  return {
    staff: staff,
    metaData: {
      pagination: {
        total,
        page,
        limit,
        totalPages: Math.ceil(total / limit),
      },
    },
  };
};

export const fetchStaffById = async (id: string) => {
  const res = await axiosInstance.get(`/v1/staff/${id}`);
  return res.data


};

export const createStaff = async (data: StaffCreateType) => {
  const res = await axiosInstance.post("/v1/staff", data);
  return res.data
};

export const updateStaff = async (id: string, data: StaffCreateType) => {
  const res = await axiosInstance.put(`/v1/staff/${id}`, data);
  return res.data
};

export const deleteStaff = async (id: string) => {
  const res = await axiosInstance.delete(`/v1/staff/${id}`);
  return res.data
};

// Legacy paginated fetch for AsyncSingleSelectField
export const fetchStaffOptions = async (
  params: AsyncSelectFetchParams
): Promise<AsyncSelectFetchResult> => {
  const { search, page, pageSize } = params;
  const { staff, metaData } = await fetchAllStaff({
    search,
    page,
    limit: pageSize,
  });

  return {
    items: staff.map((s: StaffType) => ({
      label: s.name,
      value: s.id,
    })),
    hasMore: metaData.pagination.page < metaData.pagination.totalPages,
    total: metaData.pagination.total,


  };
};
